import type { FeatureUpdate } from "./featureUpdates";

/**
 * Popups for big releases: an update marked `popup` in featureUpdates.ts opens by itself, once, for
 * every customer who has not seen it. Marking one is Haim's decision, given in the session: it
 * reaches every customer the moment it deploys, so never set `popup` without his explicit go.
 *
 * docs/rules/feature-update-popups.md
 */

/** A popup is news: from this many days after the update's date, it no longer opens by itself. */
export const POPUP_WINDOW_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

/** An update's `date` (`DD-MM-YYYY`) as the start of that day in the browser's time; null when it does not parse. */
export const updateDateOf = (date: string): Date | null => {
  const match = /^(\d{1,2})-(\d{1,2})-(\d{4})$/.exec(date.trim());
  if (!match) return null;
  const [day, month, year] = match.slice(1).map(Number);
  const parsed = new Date(year, month - 1, day);
  return parsed.getDate() === day && parsed.getMonth() === month - 1 ? parsed : null;
};

/** Firestore's Timestamp, a Date or an ISO string, as a Date; anything else as null. */
export const toDateOrNull = (value: unknown): Date | null => {
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value : null;
  if (value && typeof (value as { toDate?: () => Date }).toDate === "function") return toDateOrNull((value as { toDate: () => Date }).toDate());
  if (typeof value === "string" || typeof value === "number") return toDateOrNull(new Date(value));
  return null;
};

/**
 * The update this user is shown as a popup: the newest UNSEEN update marked `popup`, still news (its
 * date within POPUP_WINDOW_DAYS), for an account that existed on that date. A newcomer gets no
 * "what changed": they never saw the old one.
 */
export const popupCandidate = (
  unseenUpdates: readonly FeatureUpdate[],
  accountCreatedAt: Date | null,
  now: Date
): FeatureUpdate | null => {
  const candidates = unseenUpdates.filter((update) => {
    const date = update.popup ? updateDateOf(update.date) : null;
    if (!date || now.getTime() - date.getTime() >= POPUP_WINDOW_DAYS * DAY_MS) return false;
    return !accountCreatedAt || accountCreatedAt.getTime() < date.getTime();
  });
  return [...candidates].sort((a, b) => b.id - a.id)[0] ?? null;
};
